import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as TrainIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import moderationService from '../../services/moderationService';

const ModelManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useSelector((state) => state.auth);
  const [modelInfo, setModelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [trainingDialog, setTrainingDialog] = useState(false);

  useEffect(() => {
    fetchModelInfo();
  }, []);

  const fetchModelInfo = async () => {
    try {
      setLoading(true);
      const response = await moderationService.getMLModelInfo();
      setModelInfo(response);
    } catch (error) {
      console.error('Error fetching model info:', error);
      showAlert('Failed to fetch model information', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTrainModel = async () => {
    try {
      setTraining(true);
      setTrainingDialog(false);
      
      const response = await moderationService.trainMLModel();
      
      if (response.success) {
        showAlert('ML model training started successfully', 'success');
        // Refresh model info after a delay
        setTimeout(() => {
          fetchModelInfo();
        }, 2000);
      } else {
        showAlert(`Training failed: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Error training model:', error);
      showAlert('Failed to start model training', 'error');
    } finally {
      setTraining(false);
    }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => {
      setAlert({ show: false, message: '', severity: 'success' });
    }, 5000);
  };

  const getModelStatusChip = () => {
    if (!modelInfo?.model_info) return <Chip label="Unknown" color="default" />;
    
    const { trained } = modelInfo.model_info;
    
    if (trained) {
      return <Chip label="Trained" color="success" icon={<CheckCircleIcon />} />;
    } else {
      return <Chip label="Not Trained" color="warning" icon={<WarningIcon />} />;
    }
  };

  const getTrainingDataStatus = () => {
    if (!modelInfo?.training_data) return 'Unknown';
    
    const { total_completed_moderations, min_samples_for_training } = modelInfo.training_data;
    
    if (total_completed_moderations >= min_samples_for_training) {
      return 'Ready for Training';
    } else {
      return `Need ${min_samples_for_training - total_completed_moderations} more samples`;
    }
  };

  const getTrainingDataColor = () => {
    if (!modelInfo?.training_data) return 'default';
    
    const { total_completed_moderations, min_samples_for_training } = modelInfo.training_data;
    
    if (total_completed_moderations >= min_samples_for_training) {
      return 'success';
    } else {
      return 'warning';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: theme.palette.background.default,
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden'
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading model information...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.palette.background.default,
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom>
            <PsychologyIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            AI Model Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Monitor and manage the AI moderation machine learning model
          </Typography>
        </Box>

      {/* Alert */}
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert({ show: false, message: '', severity: 'success' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Model Status Cards */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        {/* Model Status */}
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Model Status
                  </Typography>
                  <Typography variant="h6">
                    {getModelStatusChip()}
                  </Typography>
                </Box>
                <PsychologyIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Training Data */}
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Training Data
                  </Typography>
                  <Typography variant="h6">
                    <Chip 
                      label={getTrainingDataStatus()} 
                      color={getTrainingDataColor()}
                      size="small"
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {modelInfo?.training_data?.total_completed_moderations || 0} samples
                  </Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Overrides */}
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Admin Overrides
                  </Typography>
                  <Typography variant="h4">
                    {modelInfo?.training_data?.admin_overrides || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Learning opportunities
                  </Typography>
                </Box>
                <SettingsIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Model Type */}
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Model Type
                  </Typography>
                  <Typography variant="h6">
                    {modelInfo?.model_info?.model_type || 'None'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {modelInfo?.model_info?.feature_count || 0} features
                  </Typography>
                </Box>
                <InfoIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h6" gutterBottom>
          Model Actions
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={training ? <CircularProgress size={20} /> : <TrainIcon />}
            onClick={() => setTrainingDialog(true)}
            disabled={training || !modelInfo?.training_data || 
              modelInfo.training_data.total_completed_moderations < modelInfo.training_data.min_samples_for_training}
            color="primary"
          >
            {training ? 'Training...' : 'Train Model'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchModelInfo}
            disabled={loading}
          >
            Refresh Status
          </Button>
        </Box>
      </Paper>

      {/* Model Information */}
      {modelInfo?.model_info && (
        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 2, sm: 3, md: 4 } }}>
          <Typography variant="h6" gutterBottom>
            Model Information
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Model Path
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {modelInfo.model_info.model_path || 'Not available'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Scaler Path
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {modelInfo.model_info.scaler_path || 'Not available'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Feature Count
              </Typography>
              <Typography variant="body1">
                {modelInfo.model_info.feature_count || 0}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Training Status
              </Typography>
              <Typography variant="body1">
                {modelInfo.model_info.trained ? 'Completed' : 'Not Trained'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Training Data Details */}
      {modelInfo?.training_data && (
        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Training Data Statistics
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Total Completed Moderations
              </Typography>
              <Typography variant="h4" color="primary">
                {modelInfo.training_data.total_completed_moderations}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Admin Overrides
              </Typography>
              <Typography variant="h4" color="secondary">
                {modelInfo.training_data.admin_overrides}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Minimum Required Samples
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {modelInfo.training_data.min_samples_for_training}
              </Typography>
            </Grid>
          </Grid>
          
          {/* Progress Bar */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Training Data Progress
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(
                (modelInfo.training_data.total_completed_moderations / 
                 modelInfo.training_data.min_samples_for_training) * 100,
                100
              )}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {modelInfo.training_data.total_completed_moderations} / {modelInfo.training_data.min_samples_for_training} samples
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Training Confirmation Dialog */}
      <Dialog open={trainingDialog} onClose={() => setTrainingDialog(false)}>
        <DialogTitle>
          <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Train ML Model
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to start training the machine learning model?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This process may take several minutes and will use all available training data to improve the model's accuracy.
          </Typography>
          
          {modelInfo?.training_data && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Training Data: {modelInfo.training_data.total_completed_moderations} samples
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admin Overrides: {modelInfo.training_data.admin_overrides} learning opportunities
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrainingDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleTrainModel} 
            variant="contained"
            startIcon={<TrainIcon />}
          >
            Start Training
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default ModelManagement;
